import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    limitedGroupOrder,
    mapLimitedCostToItem,
    mapOtherCostToItem,
    othersGroupOrder,
} from '../catalogOtherAndLimitedHelpers';
import { globalLimitedCostsGql, globalOtherCostsGql } from '../catalogOtherAndLimitedQueries';
import { CatalogOtherAndLimitedTab } from '../types';
import type {
    TCatalogOtherAndLimitedGroupRow,
    TCatalogOtherAndLimitedItem,
    TCatalogOtherAndLimitedRow,
} from '../types';

const PAGE_SIZE = 1000;

const listRequestIds = [
    'globalOtherCosts_UER',
    'globalOtherCosts_BIM',
    'globalLimitedCosts_UER',
    'globalLimitedCosts_BIM',
];

const getListVariables = (methodology: ProjectMethodologyEnum) => ({
    methodology,
    skip: 0,
    take: PAGE_SIZE,
    order: [{ id: SortEnumType.Asc }],
});

const getGroupedRows = (
    items: TCatalogOtherAndLimitedItem[],
    tab: CatalogOtherAndLimitedTab,
): TCatalogOtherAndLimitedRow[] => {
    const rowsByGroup = R.groupBy((item: TCatalogOtherAndLimitedItem) => item.groupName, items);
    const groupOrder = tab === CatalogOtherAndLimitedTab.Others ? othersGroupOrder : limitedGroupOrder;
    const remainingGroups = Object.keys(rowsByGroup).filter((groupName) => !groupOrder.includes(groupName));

    return [...groupOrder, ...remainingGroups].reduce<TCatalogOtherAndLimitedRow[]>((acc, groupName) => {
        const groupItems = rowsByGroup[groupName];

        if (!groupItems?.length) {
            return acc;
        }

        const groupRow: TCatalogOtherAndLimitedGroupRow = {
            id: `header_${groupName}`,
            name: groupName,
            isGroup: true,
        };

        return [...acc, groupRow, ...groupItems];
    }, []);
};

export const useCatalogOtherAndLimitedData = (
    tab: CatalogOtherAndLimitedTab,
    methodology: ProjectMethodologyEnum,
    nameFilter: string[] = [],
) => {
    const dispatch = useDispatch();
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;

    const [resetListData] = useResetMultipleStoreData({ requestIds: listRequestIds });

    const variables = useMemo(() => getListVariables(methodology), [methodology]);

    const [otherData, otherStatus, , otherRequest] = useRequest({
        requestId: `globalOtherCosts_${methodology}`,
        query: globalOtherCostsGql,
        withEffect: false,
    });
    const [limitedData, limitedStatus, , limitedRequest] = useRequest({
        requestId: `globalLimitedCosts_${methodology}`,
        query: globalLimitedCostsGql,
        withEffect: false,
    });

    const refetch = useCallback(() => {
        if (isOthersTab) {
            dispatch(otherRequest(variables));

            return;
        }

        dispatch(limitedRequest(variables));
    }, [dispatch, isOthersTab, limitedRequest, otherRequest, variables]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(
        () => () => {
            resetListData();
        },
        [resetListData],
    );

    const items = useMemo(() => {
        if (isOthersTab) {
            return (otherData?.globalOtherCosts?.items ?? []).map(mapOtherCostToItem);
        }

        return (limitedData?.globalLimitedCosts?.items ?? []).map(mapLimitedCostToItem);
    }, [isOthersTab, limitedData?.globalLimitedCosts?.items, otherData?.globalOtherCosts?.items]);

    const filteredItems = useMemo(() => {
        if (!nameFilter.length) {
            return items;
        }

        return items.filter((item) => nameFilter.includes(item.name));
    }, [items, nameFilter]);

    const rowData = useMemo(() => getGroupedRows(filteredItems, tab), [filteredItems, tab]);

    const totalCount = useMemo(() => {
        if (isOthersTab) {
            return otherData?.globalOtherCosts?.totalCount ?? 0;
        }

        return limitedData?.globalLimitedCosts?.totalCount ?? 0;
    }, [isOthersTab, limitedData?.globalLimitedCosts?.totalCount, otherData?.globalOtherCosts?.totalCount]);

    const isLoading = useMemo(() => {
        const status = isOthersTab ? otherStatus : limitedStatus;

        return status === RequestStatus.FETCHING && !items.length;
    }, [isOthersTab, items.length, limitedStatus, otherStatus]);

    const getItemById = useCallback((id: number) => items.find((item) => item.id === id), [items]);

    return {
        items,
        rowData,
        totalCount,
        fetchedItems: filteredItems.length,
        isLoading,
        refetch,
        getItemById,
    };
};
